import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { BaseEntityLayerManager } from '../core/base/base-entity-layer-manager';
import { IReporteBase } from 'modelos/src';

/**
 * TrackersLayerManager
 *
 * Manager para renderizar trackers en el mapa.
 *
 * Características:
 * - Sistema de DOS capas (background + foreground)
 * - Background: brújula rotable si tiene orientación, círculo fijo si no
 * - Foreground: icono de tracker siempre fijo
 * - Soporta clustering
 * - Color de cluster: púrpura (#9C27B0)
 *
 * Similar a VehiculosLayerManager pero con icono tracker.png (20x20)
 */
@Injectable({
  providedIn: 'root',
})
export class TrackersLayerManager extends BaseEntityLayerManager<
  IReporteBase<'Tracker 4G'>
> {
  // ========== CONFIGURACIÓN ==========
  readonly entityType = 'trackers';
  readonly supportsClustering = true;
  readonly clusterColor = '#9C27B0'; // Púrpura

  // IDs de layers específicos
  private backgroundLayerId = 'trackers-orientation-layer';
  private foregroundLayerId = 'trackers-layer';
  private unclusteredBackgroundLayerId =
    'trackers-unclustered-background-layer';
  private unclusteredForegroundLayerId =
    'trackers-unclustered-foreground-layer';

  // ========== RENDERIZACIÓN ==========

  /**
   * Convierte un reporte de tracker a un Feature de GeoJSON
   */
  override entityToFeature(
    tracker: IReporteBase<'Tracker 4G'>,
  ): GeoJSON.Feature<GeoJSON.Point> {
    // OPTIMIZACIÓN: Almacenar en mapa en vez de serializar
    this.storeEntity(tracker);

    const valores = tracker.valores;
    const geojson = valores?.geojson;

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: geojson?.coordinates || [0, 0],
      },
      properties: {
        id: tracker._id,
        tipo: tracker.tipoReporte,
        orientation: valores?.orientacion || null,
        hasOrientation:
          valores?.orientacion !== undefined && valores?.orientacion !== null,
        // Metadata adicional para popups
        velocidad: valores?.velocidad,
        // Datos completos para popup
      },
    };
  }

  /**
   * Expresión para el icono foreground (tracker)
   */
  override getIconExpression(): string {
    return 'tracker-icon';
  }

  /**
   * Expresión para el icono background (brújula si tiene orientación, círculo si no)
   */
  getBackgroundIconExpression(): mapboxgl.Expression {
    return ['case', ['get', 'hasOrientation'], 'compass-icon', 'circle-icon'];
  }

  /**
   * Expresión para la rotación del background
   */
  getIconRotationExpression(): mapboxgl.Expression {
    return [
      'case',
      ['get', 'hasOrientation'],
      ['get', 'orientation'],
      0, // Sin rotación si no tiene orientación
    ];
  }

  // ========== CREACIÓN DE LAYERS ==========

  /**
   * Crea todos los layers necesarios para trackers
   */
  protected override createLayers(): void {
    if (!this.map) return;

    // 1. LAYERS DE CLUSTERING
    if (this.supportsClustering) {
      this.createStandardClusterLayers();

      // Layers de trackers NO CLUSTERED (cuando clustering está ON)
      // Sistema de DOS capas para mantener consistencia visual:

      // 1a. Background unclustered (brújula/círculo)
      this.addLayer({
        id: this.unclusteredBackgroundLayerId,
        type: 'symbol',
        source: this.clusteredSourceId,
        filter: ['!', ['has', 'point_count']], // Solo features que NO son clusters
        layout: {
          'icon-image': this.getBackgroundIconExpression(),
          'icon-size': 0.05, // 800 * 0.05 = 40px (compass.svg/circle.png son 800x800)
          'icon-allow-overlap': true,
          'icon-padding': 0,
          'icon-rotation-alignment': 'map', // IMPORTANTE: 'map' para que la brújula apunte al heading correcto
          'icon-rotate': this.getIconRotationExpression(),
          'icon-pitch-alignment': 'viewport', // 'viewport' para que no se pegue al mapa inclinado
          visibility: 'none', // Inicialmente oculto (se muestra cuando clustering ON)
        },
      });

      // 1b. Foreground unclustered (icono tracker)
      this.addLayer({
        id: this.unclusteredForegroundLayerId,
        type: 'symbol',
        source: this.clusteredSourceId,
        filter: ['!', ['has', 'point_count']], // Solo features que NO son clusters
        layout: {
          'icon-image': this.getIconExpression(),
          'icon-size': 0.08, // PNG 200x200 → 16px (16/200 = 0.08) - Similar a vehiculo (18px)
          'icon-allow-overlap': true,
          'icon-padding': 0,
          'icon-rotation-alignment': 'viewport',
          'icon-pitch-alignment': 'viewport',
          visibility: 'none', // Inicialmente oculto (se muestra cuando clustering ON)
        },
      });
    }

    // 2. BACKGROUND LAYER (brújula rotable o círculo fijo)
    this.addLayer({
      id: this.backgroundLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'icon-image': this.getBackgroundIconExpression(),
        'icon-size': 0.05, // 800 * 0.05 = 40px (circle.png y compass.svg son 800x800)
        'icon-allow-overlap': true,
        'icon-padding': 0,
        'icon-rotation-alignment': 'map', // IMPORTANTE: 'map' para que la brújula apunte al heading correcto
        'icon-rotate': this.getIconRotationExpression(),
        'icon-pitch-alignment': 'viewport', // 'viewport' para que no se pegue al mapa inclinado
      },
    });

    // 3. FOREGROUND LAYER (icono de tracker)
    this.addLayer({
      id: this.foregroundLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'icon-image': this.getIconExpression(),
        'icon-size': 0.08, // PNG 200x200 → 16px (16/200 = 0.08) - Similar a vehiculo (18px)
        'icon-allow-overlap': true,
        'icon-padding': 0,
        'icon-rotation-alignment': 'viewport',
        'icon-pitch-alignment': 'viewport',
      },
    });
  }

  // ========== CLUSTERING ==========

  protected override updateClusteringVisibility(): void {
    if (!this.map || !this.supportsClustering) return;

    if (this.clusteringEnabled) {
      // CLUSTERING ON:
      // - Mostrar clusters (respetando filtro)
      // - Mostrar unclustered (background + foreground, trackers fuera de clusters, respetando filtro)
      // - Ocultar layers normales (background + foreground)
      // IMPORTANTE: Usar this.isVisible en lugar de true para respetar el estado del filtro
      this.setLayerVisibility(
        `${this.entityType}-cluster-layer`,
        this.isVisible,
      );
      this.setLayerVisibility(
        `${this.entityType}-cluster-count-layer`,
        this.isVisible,
      );
      this.setLayerVisibility(
        this.unclusteredBackgroundLayerId,
        this.isVisible,
      );
      this.setLayerVisibility(
        this.unclusteredForegroundLayerId,
        this.isVisible,
      );
      this.setLayerVisibility(this.backgroundLayerId, false);
      this.setLayerVisibility(this.foregroundLayerId, false);
    } else {
      // CLUSTERING OFF:
      // - Ocultar clusters
      // - Ocultar unclustered (background + foreground)
      // - Mostrar layers normales (background + foreground, respetando filtro)
      this.setLayerVisibility(`${this.entityType}-cluster-layer`, false);
      this.setLayerVisibility(`${this.entityType}-cluster-count-layer`, false);
      this.setLayerVisibility(this.unclusteredBackgroundLayerId, false);
      this.setLayerVisibility(this.unclusteredForegroundLayerId, false);
      // IMPORTANTE: Usar this.isVisible en lugar de true para respetar el estado del filtro
      this.setLayerVisibility(this.backgroundLayerId, this.isVisible);
      this.setLayerVisibility(this.foregroundLayerId, this.isVisible);
    }
  }

  private setLayerVisibility(layerId: string, visible: boolean): void {
    if (!this.map) return;

    if (this.map.getLayer(layerId)) {
      this.map.setLayoutProperty(
        layerId,
        'visibility',
        visible ? 'visible' : 'none',
      );
    }
  }

  // ========== VISIBILIDAD GENERAL ==========

  override setVisibility(visible: boolean): void {
    if (!this.map) return;

    this.isVisible = visible;

    // Si clustering está habilitado, manejar visibility de clusters y unclustered
    if (this.clusteringEnabled && this.supportsClustering) {
      this.setLayerVisibility(`${this.entityType}-cluster-layer`, visible);
      this.setLayerVisibility(
        `${this.entityType}-cluster-count-layer`,
        visible,
      );
      this.setLayerVisibility(this.unclusteredBackgroundLayerId, visible);
      this.setLayerVisibility(this.unclusteredForegroundLayerId, visible);
    } else {
      // Si clustering OFF, manejar visibility de layers normales
      this.setLayerVisibility(this.backgroundLayerId, visible);
      this.setLayerVisibility(this.foregroundLayerId, visible);
    }
  }
}
